import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { FileText } from "lucide-react";

// Algemene voorwaarden — kort en leesbaar; B2B (verenigingen/stichtingen).
const COMPANY = {
  name: "Club-Fin.nl", brand: "MeerjarenplanOnline.nl", contact: "Ed van Wijk",
  address: "Markt 13", city: "3252 BC Goedereede", kvk: "42158680", vat: "NL004544424B16", email: "info@club-fin.nl",
};

function Section({ title, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="font-display font-black text-xl uppercase mb-3 tracking-tight">{title}</h2>
      <div className="text-sm leading-relaxed space-y-3 text-foreground/90">{children}</div>
    </section>
  );
}

export default function TermsPage() {
  const lastUpdated = "juni 2026";
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto" data-testid="terms-page">
        <div className="mb-8">
          <div className="stat-label flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Juridisch · Voorwaarden</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase mt-1 leading-none">Algemene voorwaarden</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Versie {lastUpdated}. Deze voorwaarden gelden voor het gebruik van {COMPANY.brand}, een dienst van {COMPANY.name}.
          </p>
        </div>

        <Section title="1. Wie zijn wij" testid="terms-company">
          <div className="border-2 border-foreground p-4 bg-muted/30 font-mono-num text-sm">
            <div><b>{COMPANY.name}</b> <span className="text-muted-foreground">(handelsnaam {COMPANY.brand})</span></div>
            <div>Contactpersoon: {COMPANY.contact}</div>
            <div>{COMPANY.address}, {COMPANY.city}</div>
            <div>KvK: {COMPANY.kvk} · BTW: {COMPANY.vat}</div>
            <div>E-mail: <a className="underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></div>
          </div>
        </Section>

        <Section title="2. De dienst" testid="terms-service">
          <p>{COMPANY.brand} is een online rekentool waarmee sportclubs meerjarige investeringsscenario's doorrekenen: historische cijfers, begrotingen, leningen, reserves, projecties en exports (Excel, CSV, PDF), aangevuld met AI-ondersteuning voor het inlezen van jaarverslagen en het samenvatten van scenario's.</p>
          <p><b>Geen financieel advies.</b> De uitkomsten zijn berekeningen op basis van jouw eigen invoer en aannames. Besluiten over investeringen, leningen of begrotingen neem je zelf, zo nodig met je accountant, bank of bond. {COMPANY.name} is geen adviseur en geen partij bij die besluiten.</p>
        </Section>

        <Section title="3. Account en proefperiode" testid="terms-account">
          <ul className="list-disc pl-6 space-y-1">
            <li>Je maakt een account aan met een geldig e-mailadres. Je bent verantwoordelijk voor het geheimhouden van je wachtwoord en voor alles wat onder je account gebeurt.</li>
            <li>Nieuwe gebruikers krijgen <b>14 dagen gratis</b> volledige toegang op een demo-club. Er zijn geen betaalgegevens nodig en de proef stopt automatisch; daarna wordt je account alleen-lezen tot je een abonnement kiest.</li>
            <li>Teamleden die je uitnodigt gebruiken de dienst onder jouw abonnement en onder deze voorwaarden.</li>
          </ul>
        </Section>

        <Section title="4. Abonnement, prijs en betaling" testid="terms-billing">
          <ul className="list-disc pl-6 space-y-1">
            <li>Abonnementen (Standaard, Premium) lopen per <b>jaar</b> en worden vooruit betaald via Mollie (iDEAL of creditcard). De actuele prijzen staan op de prijzenpagina en zijn <b>inclusief 21% BTW</b>.</li>
            <li>Bij de eerste betaling geef je een machtiging (SEPA-incasso of kaartmandaat). Het abonnement <b>verlengt automatisch</b> met een jaar tegen de dan geldende prijs; je ontvangt daarvan een factuur per e-mail.</li>
            <li>Opzeggen kan op elk moment via je accountpagina. Je houdt toegang tot het einde van de betaalde periode; er vindt geen terugbetaling plaats over de resterende maanden.</li>
            <li>Kortingscodes zijn persoonlijk, eenmalig en gelden zoals bij de code vermeld (alleen het eerste jaar, of ook op verlengingen).</li>
            <li>Mislukt een verlengingsbetaling, dan word je per e-mail geïnformeerd en kan het account worden teruggezet naar alleen-lezen tot de betaling is voldaan.</li>
          </ul>
        </Section>

        <Section title="5. Gebruik" testid="terms-use">
          <ul className="list-disc pl-6 space-y-1">
            <li>Je gebruikt de dienst alleen voor de eigen club(s) of organisaties waarvoor je bevoegd bent en niet voor doorverkoop.</li>
            <li>Je voert geen gegevens in waarvoor je geen toestemming hebt en probeert de dienst niet te omzeilen, te overbelasten of te reverse-engineeren.</li>
            <li>Jij blijft eigenaar van de cijfers en documenten die je invoert; wij gebruiken die alleen om de dienst voor jou te leveren (zie de <Link to="/privacy" className="underline">privacyverklaring</Link>).</li>
          </ul>
        </Section>

        <Section title="6. Beschikbaarheid en wijzigingen" testid="terms-availability">
          <p>We doen ons best de dienst 24/7 beschikbaar te houden, maar garanderen geen ononderbroken werking. Onderhoud kondigen we waar mogelijk vooraf aan. We mogen functies verbeteren of aanpassen; wezenlijke beperkingen van betaalde functionaliteit melden we vooraf per e-mail.</p>
        </Section>

        <Section title="7. Aansprakelijkheid" testid="terms-liability">
          <p>De dienst wordt geleverd zoals hij is. {COMPANY.name} is niet aansprakelijk voor beslissingen die je op basis van berekeningen neemt, voor indirecte schade of gevolgschade, of voor verlies van gegevens die je niet zelf hebt geëxporteerd. Voor zover aansprakelijkheid toch bestaat, is die beperkt tot het bedrag dat je in de twaalf maanden vóór de gebeurtenis voor het abonnement hebt betaald. Deze beperkingen gelden niet bij opzet of bewuste roekeloosheid.</p>
        </Section>

        <Section title="8. Intellectueel eigendom" testid="terms-ip">
          <p>De software, rekenmodellen, vormgeving en teksten van {COMPANY.brand} zijn eigendom van {COMPANY.name}. Je krijgt een niet-overdraagbaar gebruiksrecht voor de duur van je abonnement. Exports die je maakt (rapporten, PDF's) mag je vrij binnen je club gebruiken.</p>
        </Section>

        <Section title="9. Beëindiging" testid="terms-termination">
          <p>Bij misbruik, wanbetaling of overtreding van deze voorwaarden mogen we een account opschorten of beëindigen. Na beëindiging kun je je gegevens nog 30 dagen exporteren; daarna worden ze verwijderd of gepseudonimiseerd volgens de privacyverklaring (factuurgegevens bewaren we 7 jaar volgens de fiscale bewaarplicht).</p>
        </Section>

        <Section title="10. Toepasselijk recht" testid="terms-law">
          <p>Op deze voorwaarden is Nederlands recht van toepassing. Geschillen leggen we eerst in goed overleg voor aan elkaar; komen we er niet uit, dan is de rechtbank van het arrondissement waarin {COMPANY.name} is gevestigd bevoegd. Wijzigen we deze voorwaarden inhoudelijk, dan informeren we actieve klanten per e-mail; de meest recente versie staat altijd op deze pagina.</p>
        </Section>

        <div className="mt-10 flex gap-4 text-sm">
          <Link to="/privacy" className="text-muted-foreground hover:text-foreground underline">Privacyverklaring</Link>
          <Link to="/" className="text-muted-foreground hover:text-foreground underline">← Terug naar dashboard</Link>
        </div>
      </div>
    </AppLayout>
  );
}
